/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { Link } from 'react-router'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell } from '../../../table/TableComponents'
import { DocumentActionMenu } from './DocumentActionMenu'
import DocIcon from '../../../shared/DocIcon'
import DocState from '../../../shared/DocState'
import ValidDocIndicator from '../../../shared/ValidDocIndicator'
import { FsiType, AclLevel } from '../../../../constants/fsi'
import DueDateIndicator from '../../../DueDateIndicator'

interface RecentDocsTableProps {
  // No props needed - data comes from RcmTableDataWrapper context
}

export const RecentDocsTable: React.FC<RecentDocsTableProps> = () => {
  const {
    service: {
      filter: { translate, date },
      init
    }
  } = useServiceContext()
  const tableData = useRcmTableData()
  const isEtmfEnabled = init.getFeaturesInfo()?.etmfFeature?.show || false

  useEffect(() => {
    // Refresh table on mount (similar to original)
    console.log('RecentDocsTable - refreshing table on mount')
    tableData.refreshTable()
  }, []) // Empty dependency array to run only on mount

  return (
    <RcmTable
      entries={tableData.entries}
      isLoading={tableData.isLoading}
      className="chita-table fsiTable dashboard-table"
      noResultsText="NO_RESULTS_FILTERED"
      hidePaginate={true}
      disableOnInitLoad={false}
      TableFooter={
        <tr>
          <td colSpan={3} className="view-all">
            <Link to="/documents/recent">
              {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
            </Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="9609cc50-234c-4b9a-b840-26f3da09efab">{translate('NAME')}</TableHeadCell>
          <TableHeadCell id="6a0ce50a-d3dc-4d0a-bb0d-3a41921150b3">{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
          <TableHeadCell id="02caaf07-ccdc-4b56-9546-ba7c1af12185" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props) => {
        const { row } = props

        return (
          <tr key={row.id}>
            <td className="name clipped-cell with-thumb">
              <div className="d-flex align-left">
                {row.type !== FsiType.FOLDER && <ValidDocIndicator fsi={row} />}
                <DocIcon fsi={row} />
                <div className="ellipsis" title={row.name}>
                  {row.type === FsiType.PLACEHOLDER ||
                  (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
                  !row.valid ? (
                    <span>
                      {row.name}
                      {isEtmfEnabled && row.type === FsiType.PLACEHOLDER && (
                        <DueDateIndicator dueDateStatus={row.dueDateStatus} translate={translate} />
                      )}
                    </span>
                  ) : (
                    <Link to={`/show-file/${row.id}`} className="doc-name" title={row.name}>
                      {row.name}
                    </Link>
                  )}
                </div>
                <DocState fsi={row} />
              </div>
            </td>
            <td className="narrow updated">{row.updatedOn ? date(row.updatedOn) : translate('NOT_AVAILABLE')}</td>
            <td className="actions">
              <DocumentActionMenu item={row} />
            </td>
          </tr>
        )
      }}
    />
  )
}
